import { createAsyncThunk } from "@reduxjs/toolkit";
import { toastr } from "react-redux-toastr";
import {
  userConstants,
  brandConstants,
  campaignConstants,
  LOCAL_STORAGE,
  HTTP_STATUS,
  USER_ROLE,
} from "@/config/constants";
import { authService } from "@/features/auth/services/authService";
import { setLocal } from "@/helpers/localStore";
import MESSAGE from "@/helpers/messages";
import { getUserRole, IS_WHITE_LABELED_DOMAIN } from "@/config/appConfig";
import { getSubdomain as getSubdomainFromTldts } from "tldts";
import { rpGTM } from "@/config/rpGTM";

type LoginPayload = Record<string, unknown>;

type LoginBrand = {
  id?: number | string;
  is_white_label?: boolean;
  subdomain?: string;
  [key: string]: unknown;
};

type LoginUserDetail = {
  id?: number | string;
  email?: string;
  fk_role_id?: number | string;
  is_join_camp_tnc_checked?: boolean;
  is_master_pass?: boolean;
  role?: {
    name?: string;
  };
  brandData?: LoginBrand[];
  [key: string]: unknown;
};

function getSubdomain() {
  try {
    return getSubdomainFromTldts(window.location.hostname, {
      validHosts: ["localhost"],
    });
  } catch {
    return null;
  }
}

function resolveSelectedBrand(user_detail?: LoginUserDetail) {
  const role = getUserRole(user_detail?.fk_role_id);
  const brands = user_detail?.brandData || [];
  if (!brands.length) return null;

  if (role === USER_ROLE.PARENT_COMPANY.NAME) {
    if (brands.length === 1) return brands[0];
    const whiteLabelBrand = brands.find((b) => b?.is_white_label);
    const nonWhiteLabelBrand = brands.find((b) => !b?.is_white_label);
    if (IS_WHITE_LABELED_DOMAIN) {
      const subdomainBrand = brands.find((b) => b.subdomain === getSubdomain());
      if (subdomainBrand?.is_white_label) return subdomainBrand;
      return whiteLabelBrand || nonWhiteLabelBrand;
    }
    return nonWhiteLabelBrand || whiteLabelBrand;
  }
  return brands[0];
}

export const loginThunk = createAsyncThunk(
  "auth/login",
  async (payload: LoginPayload, { dispatch, rejectWithValue }) => {
    try {
      const res = await authService.login(payload);
      const { token, user_detail } = res.data as {
        token?: string;
        user_detail?: LoginUserDetail;
      };

      if (res.status !== HTTP_STATUS.OK || !token)
        return rejectWithValue("Login failed");

      if (!user_detail?.brandData?.length) {
        toastr.error("Error", MESSAGE.brandDataMissing);
        return rejectWithValue(MESSAGE.brandDataMissing);
      }

      setLocal(LOCAL_STORAGE.TOKEN_NAME, token);

      rpGTM.login({
        userId: user_detail.id,
        userEmail: user_detail.email,
        userRole: user_detail?.is_master_pass
          ? "Admin"
          : user_detail?.role?.name || getUserRole(user_detail?.fk_role_id),
      });

      dispatch({ type: userConstants.GET_USER_SUCCESS, payload: user_detail });
      dispatch({
        type: brandConstants.GET_BRANDS_SUCCESS,
        payload: user_detail.brandData,
      });

      const role = getUserRole(user_detail?.fk_role_id);
      if (
        role === USER_ROLE.BRAND_OWNER.NAME ||
        role === USER_ROLE.BRAND_USER.NAME
        // role === USER_ROLE.PARENT_COMPANY.NAME
      ) {
        const selectedBrand = resolveSelectedBrand(user_detail);
        if (selectedBrand)
          dispatch({
            type: brandConstants.SET_GLOBAL_BRAND,
            payload: selectedBrand,
          });
      }

      dispatch({
        type: campaignConstants.SET_JOIN_CAMP_TNC_CHECKED,
        payload: user_detail?.is_join_camp_tnc_checked || false,
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(err?.message || "Login failed");
    }
  }
);
